import styled from 'styled-components';
import { Chip, ChipColorType } from '../chip/Chip';

interface RadioCardProps {
  title: string;
  label?: string;
  isActive: boolean;
  disabled?: boolean;
  onClick?: () => void;
  chip?: {
    color: ChipColorType;
    text: string;
  };
}

export const RadioCard = ({ title, label, isActive, disabled = false, onClick, chip }: RadioCardProps) => {
  return (
    <Container
      isActive={isActive}
      disabled={disabled}
      onClick={() => {
        if (disabled || !onClick) {
          return;
        }
        onClick();
      }}
    >
      <InputTextContainer>
        {label && <InputLabel disabled={disabled}>{label}</InputLabel>}
        <InputTitleContainer>
          <InputTitle disabled={disabled}>{title}</InputTitle>
          {chip && (
            <Chip type={'square'} color={chip.color} theme={'light'}>
              {chip.text}
            </Chip>
          )}
        </InputTitleContainer>
      </InputTextContainer>

      {isActive ? (
        <CheckedRadio>
          <CheckedIcon />
        </CheckedRadio>
      ) : (
        <DefaultRadio disabled={disabled} />
      )}
    </Container>
  );
};

const Container = styled.div<{ isActive: boolean; disabled: boolean }>`
  width: 100%;
  height: 72px;

  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 30px 16px;

  border-radius: var(--radius-medium, 8px);
  border: ${(props) => (props.isActive ? '1px solid #445EFF' : '1px solid #f2f2f7')};
  background: ${(props) => (props.isActive ? '#E5E9FF' : '#f2f2f7')};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
`;

const InputTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  gap: 8px;
`;

const InputTitleContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--radius-small, 4px);
`;

const InputLabel = styled.span<{ disabled: boolean }>`
  color: ${(props) =>
    props.disabled
      ? 'var(--Light-Scheme-Monochrome-grey30, #D5D5E3)'
      : 'var(--Light-Scheme-Monochrome-grey100, #858894)'};

  /* System/Small/s-regular */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 10.2px */
`;

const InputTitle = styled.span<{ disabled: boolean }>`
  color: ${(props) =>
    props.disabled
      ? 'var(--Light-Scheme-Monochrome-grey70, #A7A9B6)'
      : 'var(--Light-Scheme-Monochrome-offblack, #505363)'};

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */
`;
const DefaultRadio = styled.div<{ disabled: boolean }>`
  width: 16px;
  height: 16px;

  display: flex;
  align-items: center;

  gap: 8px;
  padding: 4px;

  border-radius: 50px;
  border: 1px solid var(--Light-Scheme-Monochrome-grey30, #d5d5e3);
  background: ${(props) =>
    props.disabled ? 'var(--Light-Scheme-Monochrome-grey10, #F2F2F7)' : 'var(--Light-Scheme-Monochrome-white, #fff)'};
`;

const CheckedRadio = styled.div`
  width: 16px;
  height: 16px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 2px;
  gap: 8px;

  border-radius: 50px;
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const CheckedIcon = styled.div`
  flex: 1 0 0;
  align-self: stretch;

  border-radius: 50px;
  background: var(--Light-Scheme-Color-primary-blue, #445eff);
`;
